import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import {
  type Mode,
  type Theme,
  DEFAULT_MODE,
  DEFAULT_THEME,
  LOCAL_STORAGE_MODE_KEY,
  THEME_SUPPORTS_DARK,
} from '@/lib/theme';

export interface ThemeContextValue {
  theme: Theme;
  mode: Mode;
  preferredMode: Mode;
  setTheme: (theme: Theme) => void;
  setMode: (mode: Mode) => void;
  toggleMode: () => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function getStoredMode(): Mode {
  try {
    const stored = localStorage.getItem(LOCAL_STORAGE_MODE_KEY);
    if (stored === 'light' || stored === 'dark') return stored;
  } catch {
    // localStorage unavailable
  }
  return DEFAULT_MODE;
}

function resolveMode(theme: Theme, mode: Mode): Mode {
  if (!THEME_SUPPORTS_DARK[theme] && mode === 'dark') return 'light';
  return mode;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(DEFAULT_THEME);
  const [mode, setModeState] = useState<Mode>(getStoredMode);

  const effectiveMode = resolveMode(theme, mode);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'bosch');
    document.documentElement.setAttribute('data-mode', mode);
  }, [mode]);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
  }, []);

  const setMode = useCallback((m: Mode) => {
    setModeState(m);
    try {
      localStorage.setItem(LOCAL_STORAGE_MODE_KEY, m);
    } catch {
      // localStorage unavailable
    }
  }, []);

  const toggleMode = useCallback(() => {
    setMode(mode === 'light' ? 'dark' : 'light');
  }, [mode, setMode]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      theme,
      mode: effectiveMode,
      preferredMode: mode,
      setTheme,
      setMode,
      toggleMode,
    }),
    [theme, effectiveMode, mode, setTheme, setMode, toggleMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
